import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Users, ScrollText, Phone, Package, Wallet } from "lucide-react";
import { CB_RULES } from "@/lib/cbRules";

const ICONS = { goms: Users, "go-info": ScrollText, fancalls: Phone, "supply-fee": Package, payment: Wallet };

const GomCard = ({ g }) => (
  <div data-testid={`cb-gom-${g.name.toLowerCase()}`} className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5 sm:p-6">
    <div className="font-display text-2xl font-extrabold tracking-tight">{g.name} <span className="text-zinc-500 font-semibold">| {g.shop}</span></div>
    <ul className="mt-4 space-y-2">
      {g.facts.map((f) => (
        <li key={f} className="flex gap-2.5 text-sm text-zinc-300"><span aria-hidden="true">{g.mark}</span><span>{f}</span></li>
      ))}
    </ul>
  </div>
);

const RuleItem = ({ item, i }) => (
  <li className="flex gap-4">
    <span className="font-mono text-xs text-amber-300/80 pt-1 w-6 shrink-0">{String(i + 1).padStart(2, "0")}</span>
    <div className="min-w-0">
      {item.title && <div className="font-semibold text-white underline decoration-amber-400/60 underline-offset-4">{item.title}</div>}
      <p className={`text-sm sm:text-base text-zinc-300 leading-relaxed ${item.title ? "mt-1.5" : ""}`}>{item.body}</p>
      {item.steps && (
        <ol className="mt-2 space-y-1.5">
          {item.steps.map((s, n) => (
            <li key={s} className="flex gap-2 text-sm text-zinc-300"><span className="font-mono text-amber-300">{n + 1}.</span><span>{s}</span></li>
          ))}
        </ol>
      )}
    </div>
  </li>
);

const Panel = ({ cat }) => (
  <div key={cat.key} data-testid={`cb-rules-panel-${cat.key}`} className="rise rounded-3xl border border-amber-400/15 bg-zinc-900/70 p-6 sm:p-9">
    <h2 className="font-display text-base md:text-lg font-bold uppercase tracking-[0.2em] text-amber-300">{cat.label}</h2>
    <div className="mt-6">
      {cat.goms && <div className="grid md:grid-cols-2 gap-4">{cat.goms.map((g) => <GomCard key={g.name} g={g} />)}</div>}
      {cat.items && <ol className="space-y-6">{cat.items.map((it, i) => <RuleItem key={i} item={it} i={i} />)}</ol>}
      {cat.paragraph && <p className="text-sm sm:text-base text-zinc-300 leading-relaxed max-w-3xl">{cat.paragraph}</p>}
    </div>
  </div>
);

export default function CBRules() {
  const [active, setActive] = useState(CB_RULES[0].key);
  const cat = CB_RULES.find((c) => c.key === active);
  return (
    <div className="rise">
      <Link to="/" data-testid="cb-rules-back" className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors">
        <ArrowLeft className="h-4 w-4" /> Home
      </Link>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-4">CB Rules</h1>
      <p className="text-zinc-400 mt-3 text-sm md:text-base max-w-xl">Please be sure to have reviewed the CB Rules so that we can have a smooth CB!</p>
      <div className="mt-8 grid grid-cols-2 lg:grid-cols-5 gap-2.5" role="tablist" data-testid="cb-rules-categories">
        {CB_RULES.map((c) => {
          const Icon = ICONS[c.key];
          const on = c.key === active;
          return (
            <button key={c.key} type="button" role="tab" aria-selected={on} data-testid={`cb-rules-tab-${c.key}`} onClick={() => setActive(c.key)}
              className={`flex items-center gap-2.5 rounded-2xl border px-4 py-3.5 text-left text-sm font-semibold transition-[transform,background-color,border-color,color] duration-200 hover:-translate-y-0.5 ${on ? "border-amber-400/60 bg-amber-400 text-zinc-950" : "border-white/10 bg-zinc-900/70 text-zinc-300 hover:border-amber-400/40 hover:text-white"}`}>
              <Icon className="h-4 w-4 shrink-0" /> {c.label}
            </button>
          );
        })}
      </div>
      <div className="mt-6"><Panel key={cat.key} cat={cat} /></div>
    </div>
  );
}
